import React, { useState, useEffect } from "react";

const TDSCalculator = () => {
  const [transactionAmount, setTransactionAmount] = useState(100000);
  const [tdsCategory, setTdsCategory] = useState("salary");
  const [panAvailable, setPanAvailable] = useState(true);
  const [tdsRate, setTdsRate] = useState(10);
  const [financialYear, setFinancialYear] = useState("2023-24");
  const [result, setResult] = useState({
    transactionAmount: "0.00",
    applicableTdsRate: "0.00",
    tdsAmount: "0.00",
    surcharge: "0.00",
    cess: "0.00",
    totalTds: "0.00",
    netPayable: "0.00",
    category: "",
    savingTip: "",
  });

  // TDS Calculation Function
  const calculateTDS = () => {
    let amount = parseFloat(transactionAmount);
    let rate = parseFloat(tdsRate);

    if (isNaN(amount) || amount < 0 || isNaN(rate) || rate < 0) return;

    // Adjust TDS rate based on PAN availability
    let applicableRate = rate;
    if (!panAvailable) {
      applicableRate = 20; // Higher rate if PAN not available
    }

    // Calculate base TDS
    let tdsAmount = (amount * applicableRate) / 100;
    
    // Surcharge calculation (on TDS amount for very high transactions)
    let surcharge = 0;
    if (amount > 5000000 && amount <= 10000000) {
      surcharge = tdsAmount * 0.10;
    } else if (amount > 10000000) {
      surcharge = tdsAmount * 0.15;
    }
    
    // Health and Education Cess (4% on TDS + Surcharge)
    const cess = (tdsAmount + surcharge) * 0.04;
    
    // Total TDS
    const totalTds = tdsAmount + surcharge + cess;
    
    // Net payable amount
    const netPayable = amount - totalTds;

    let category = "";
    let savingTip = "";

    switch(tdsCategory) {
      case "salary":
        category = "Salary (Section 192)";
        savingTip = "Submit investment proofs to reduce TDS. Claim HRA, 80C, 80D deductions.";
        break;
      case "interest":
        category = "Interest on Securities (Section 193)";
        savingTip = "Submit Form 15G/15H if total income below taxable limit to avoid TDS.";
        break;
      case "contractor":
        category = "Contractor Payment (Section 194C)";
        savingTip = "Ensure PAN is provided to avoid higher 20% TDS rate.";
        break;
      case "professional":
        category = "Professional Fees (Section 194J)";
        savingTip = "Keep proper invoices and PAN details to claim TDS credit in ITR.";
        break;
      case "rent":
        category = "Rent Payment (Section 194I)";
        savingTip = "TDS applicable on rent above ₹2.4L annually. Deduct monthly for compliance.";
        break;
      case "commission":
        category = "Commission/Brokerage (Section 194H)";
        savingTip = "Quarterly TDS return filing mandatory. Issue TDS certificates to payees.";
        break;
      default:
        category = "Other TDS Category";
        savingTip = "Ensure timely TDS deposit and return filing to avoid penalties.";
    }

    if (!panAvailable) {
      savingTip = "TDS at 20% due to no PAN. Provide PAN to reduce TDS rate significantly.";
    }

    setResult({
      transactionAmount: amount.toFixed(2),
      applicableTdsRate: applicableRate.toFixed(2),
      tdsAmount: tdsAmount.toFixed(2),
      surcharge: surcharge.toFixed(2),
      cess: cess.toFixed(2),
      totalTds: totalTds.toFixed(2),
      netPayable: netPayable.toFixed(2),
      category,
      savingTip,
    });
  };

  useEffect(() => {
    calculateTDS();
  }, []);

  return (
    <div className="calculator-wrapper">
      <div className="calculator-container">
        {/* Header */}
        <div className="calculator-header">
          <div className="header-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: 'white'}}>
              <rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect>
              <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
            </svg>
          </div>
          <h1>Tax Deducted At Source (TDS) Calculator</h1>
          <p>
            Calculate Tax Deducted at Source (TDS) for various payment categories under Income Tax Act
          </p>
        </div>

        {/* Main Calculator Card */}
        <div className="calculator-card">
          <div className="calculator-grid">
            {/* Left Side - Input Section */}
            <div className="input-section">
              <h2 className="section-title">
                <svg className="section-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="1" x2="12" y2="23"></line>
                  <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                </svg>
                Transaction Details
              </h2>

              {/* Transaction Amount */}
              <div className="input-group">
                <label>Transaction Amount</label>
                <div className="input-wrapper">
                  <span className="input-prefix">₹</span>
                  <input
                    type="number"
                    value={transactionAmount}
                    onChange={(e) => setTransactionAmount(e.target.value)}
                    className="calc-input"
                    placeholder="100000"
                  />
                </div>
                <div className="quick-select-group">
                  {[50000, 100000, 500000, 1000000].map((amount) => (
                    <button
                      key={amount}
                      onClick={() => setTransactionAmount(amount)}
                      className="quick-select-btn primary"
                    >
                      ₹{amount >= 100000 ? `${(amount / 100000).toFixed(0)}L` : `${(amount / 1000).toFixed(0)}K`}
                    </button>
                  ))}
                </div>
              </div>

              {/* TDS Category */}
              <div className="input-group">
                <label>TDS Category</label>
                <div className="quick-select-group">
                  {[
                    { value: "salary", label: "Salary (192)" },
                    { value: "interest", label: "Interest (193)" },
                    { value: "contractor", label: "Contractor (194C)" }
                  ].map((type) => (
                    <button
                      key={type.value}
                      onClick={() => {
                        setTdsCategory(type.value);
                        if (type.value === "salary") setTdsRate(10);
                        else if (type.value === "interest") setTdsRate(10);
                        else if (type.value === "contractor") setTdsRate(1);
                      }}
                      className={`quick-select-btn ${tdsCategory === type.value ? 'primary' : 'secondary'}`}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
                <div className="quick-select-group" style={{marginTop: '0.5rem'}}>
                  {[
                    { value: "professional", label: "Professional (194J)" },
                    { value: "rent", label: "Rent (194I)" },
                    { value: "commission", label: "Commission (194H)" }
                  ].map((type) => (
                    <button
                      key={type.value}
                      onClick={() => {
                        setTdsCategory(type.value);
                        if (type.value === "professional") setTdsRate(10);
                        else if (type.value === "rent") setTdsRate(10);
                        else if (type.value === "commission") setTdsRate(5);
                      }}
                      className={`quick-select-btn ${tdsCategory === type.value ? 'primary' : 'secondary'}`}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* TDS Rate */}
              <div className="input-group">
                <label>TDS Rate (%)</label>
                <div className="input-wrapper">
                  <span className="input-prefix">%</span>
                  <input
                    type="number"
                    value={tdsRate}
                    onChange={(e) => setTdsRate(e.target.value)}
                    className="calc-input"
                    placeholder="10"
                    step="0.1"
                  />
                </div>
                <div className="quick-select-group">
                  {[1, 2, 5, 10].map((rate) => (
                    <button
                      key={rate}
                      onClick={() => setTdsRate(rate)}
                      className="quick-select-btn success"
                    >
                      {rate}%
                    </button>
                  ))}
                </div>
              </div>

              {/* PAN Available */}
              <div className="input-group">
                <label>PAN Card Available?</label>
                <div className="quick-select-group">
                  {[
                    { value: true, label: "Yes (Normal Rate)" },
                    { value: false, label: "No (20% Rate)" }
                  ].map((type) => (
                    <button
                      key={type.value.toString()}
                      onClick={() => setPanAvailable(type.value)}
                      className={`quick-select-btn ${panAvailable === type.value ? 'success' : 'secondary'}`}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Financial Year */}
              <div className="input-group">
                <label>Financial Year</label>
                <div className="quick-select-group">
                  {[
                    { value: "2023-24", label: "FY 2023-24" },
                    { value: "2024-25", label: "FY 2024-25" },
                    { value: "2025-26", label: "FY 2025-26" }
                  ].map((type) => (
                    <button
                      key={type.value}
                      onClick={() => setFinancialYear(type.value)}
                      className={`quick-select-btn ${financialYear === type.value ? 'primary' : 'secondary'}`}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
              </div>

              <button onClick={calculateTDS} className="calculate-btn">
                Calculate TDS
              </button>
            </div>

            {/* Right Side - Results Section */}
            <div className="result-section">
              <h2 className="section-title">
                <svg className="section-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <circle cx="12" cy="12" r="6"></circle>
                  <circle cx="12" cy="12" r="2"></circle>
                </svg>
                TDS Calculation
              </h2>

              <div className="result-card">
                {/* Total TDS - Highlighted */}
                <div className="result-item highlight">
                  <div className="result-item-header">
                    <span className="result-label">Total TDS to Deduct</span>
                    <div className="result-icon-wrapper white">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: 'white'}}>
                        <rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect>
                        <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.totalTds).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    {result.category} at {result.applicableTdsRate}%
                  </div>
                </div>

                {/* Net Payable Amount */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Net Payable Amount</span>
                    <div className="result-icon-wrapper green">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#10b981'}}>
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.netPayable).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    Amount after TDS deduction
                  </div>
                </div>

                {/* Base TDS Amount */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Base TDS Amount</span>
                    <div className="result-icon-wrapper blue">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#3b82f6'}}>
                        <line x1="12" y1="1" x2="12" y2="23"></line>
                        <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.tdsAmount).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    At {result.applicableTdsRate}% rate
                  </div>
                </div>

                {/* Transaction Amount */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Gross Transaction Amount</span>
                    <div className="result-icon-wrapper purple">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#9333ea'}}>
                        <rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect>
                        <line x1="1" y1="10" x2="23" y2="10"></line>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.transactionAmount).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    Total payment amount
                  </div>
                </div>

                {/* Cess */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Health & Education Cess</span>
                    <div className="result-icon-wrapper green">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#10b981'}}>
                        <circle cx="12" cy="12" r="10"></circle>
                        <polyline points="12 6 12 12 16 14"></polyline>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.cess).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    4% on TDS + Surcharge
                  </div>
                </div>

                {/* Visual Breakdown */}
                <div className="breakdown-section">
                  <h3 className="breakdown-title">TDS Breakdown</h3>
                  
                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <span className="breakdown-label">Base TDS ({result.applicableTdsRate}%)</span>
                      <span className="breakdown-percentage">
                        ₹{Number(result.tdsAmount).toLocaleString("en-IN")}
                      </span>
                    </div>
                    <div className="breakdown-bar">
                      <div
                        className="breakdown-bar-fill blue"
                        style={{
                          width: `${parseFloat(result.totalTds) > 0 ? (parseFloat(result.tdsAmount) / parseFloat(result.totalTds)) * 100 : 0}%`,
                        }}
                      />
                    </div>
                  </div>

                  {parseFloat(result.surcharge) > 0 && (
                    <div className="breakdown-item">
                      <div className="breakdown-header">
                        <span className="breakdown-label">Surcharge</span>
                        <span className="breakdown-percentage">
                          ₹{Number(result.surcharge).toLocaleString("en-IN")}
                        </span>
                      </div>
                      <div className="breakdown-bar">
                        <div
                          className="breakdown-bar-fill purple"
                          style={{
                            width: `${(parseFloat(result.surcharge) / parseFloat(result.totalTds)) * 100}%`,
                          }}
                        />
                      </div>
                    </div>
                  )}

                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <span className="breakdown-label">Cess (4%)</span>
                      <span className="breakdown-percentage">
                        ₹{Number(result.cess).toLocaleString("en-IN")}
                      </span>
                    </div>
                    <div className="breakdown-bar">
                      <div
                        className="breakdown-bar-fill green"
                        style={{
                          width: `${parseFloat(result.totalTds) > 0 ? (parseFloat(result.cess) / parseFloat(result.totalTds)) * 100 : 0}%`,
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Payment Distribution */}
                <div className="breakdown-section" style={{marginTop: '1.5rem'}}>
                  <h3 className="breakdown-title">Payment Distribution</h3>
                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <span className="breakdown-label">Net Payment to Payee</span>
                      <span className="breakdown-percentage">
                        {((parseFloat(result.netPayable) / parseFloat(result.transactionAmount)) * 100).toFixed(2)}% (₹{Number(result.netPayable).toLocaleString("en-IN")})
                      </span>
                    </div>
                    <div className="breakdown-bar">
                      <div
                        className="breakdown-bar-fill green"
                        style={{
                          width: `${(parseFloat(result.netPayable) / parseFloat(result.transactionAmount)) * 100}%`,
                        }}
                      />
                    </div>
                  </div>
                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <span className="breakdown-label">TDS Deducted</span>
                      <span className="breakdown-percentage">
                        {((parseFloat(result.totalTds) / parseFloat(result.transactionAmount)) * 100).toFixed(2)}% (₹{Number(result.totalTds).toLocaleString("en-IN")})
                      </span>
                    </div>
                    <div className="breakdown-bar">
                      <div
                        className="breakdown-bar-fill blue"
                        style={{
                          width: `${(parseFloat(result.totalTds) / parseFloat(result.transactionAmount)) * 100}%`,
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Saving Tip Box */}
                <div className="breakdown-section" style={{marginTop: '1.5rem', padding: '1rem', background: '#f0fdf4', borderRadius: '8px', border: '1px solid #10b981'}}>
                  <h3 className="breakdown-title" style={{color: '#065f46', marginBottom: '0.5rem'}}>TDS Compliance Tip</h3>
                  <p style={{fontSize: '0.875rem', color: '#065f46', lineHeight: '1.5', margin: 0}}>
                    {result.savingTip} Issue Form 16/16A to deductees and file TDS returns on time.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Info Section */}
        <div className="info-section">
          <div className="info-header">
            <div className="info-icon-wrapper">
              <svg className="info-icon" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="16" x2="12" y2="12"></line>
                <line x1="12" y1="8" x2="12.01" y2="8"></line>
              </svg>
            </div>
            <div>
              <h3>Understanding TDS in India</h3>
              <p>
                Tax Deducted at Source (TDS) is a system where tax is collected at the source of income generation. The 
                payer deducts tax before making payment and deposits it with the government. TDS applies to various 
                payments: salary (Section 192), interest (Section 193), contractor payments (Section 194C), professional 
                fees (Section 194J), rent (Section 194I), commission (Section 194H), and many others. Each section has 
                different rates and thresholds. If PAN is not provided, TDS is deducted at 20% regardless of the 
                applicable rate. TDS must be deposited by 7th of next month (quarterly for certain deductors). TDS 
                returns (Form 24Q for salary, 26Q for non-salary) must be filed quarterly. Deductees can claim TDS 
                credit in their income tax returns using Form 26AS or AIS. Health and Education Cess at 4% is added on 
                TDS plus surcharge.
              </p>
            </div>
          </div>

          <div className="formula-section">
            <h4>TDS Calculation Formula:</h4>
            <div className="formula-display">
              Total TDS = Base TDS + Surcharge + Health & Education Cess (4%)
            </div>

            <div className="formula-params">
              <div className="formula-param">
                <div className="formula-param-key">Base TDS</div>
                <div className="formula-param-value">Transaction Amount × TDS Rate %</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">Surcharge</div>
                <div className="formula-param-value">10-15% on TDS for high-value transactions</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">Cess</div>
                <div className="formula-param-value">4% on (Base TDS + Surcharge)</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">No PAN Rate</div>
                <div className="formula-param-value">20% if PAN not provided by payee</div>
              </div>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="faq-section">
            <h3>Frequently Asked Questions</h3>

            <div className="faq-item">
              <h5>What happens if TDS is not deducted?</h5>
              <p>
                Deductor faces penalty, interest charges, and potential legal action. TDS must be deducted as per applicable sections and rates.
              </p>
            </div>

            <div className="faq-item">
              <h5>How can I claim TDS credit?</h5>
              <p>
                TDS automatically reflects in Form 26AS. While filing ITR, TDS is auto-populated and adjusted against your tax liability.
              </p>
            </div>

            <div className="faq-item">
              <h5>What is Form 15G/15H?</h5>
              <p>
                Form 15G (below 60 years) or 15H (60+ years) can be submitted if total income is below taxable limit to avoid TDS deduction.
              </p>
            </div>

            <div className="faq-item">
              <h5>When should TDS be deposited?</h5>
              <p>
                By 7th of next month for monthly deductors. Government deductors deposit by same day. Quarterly for small deductors under certain conditions.
              </p>
            </div>

            <div className="faq-item">
              <h5>What is the penalty for late TDS deposit?</h5>
              <p>
                Interest at 1.5% per month on delayed payment plus penalty under Section 271C up to amount of TDS. Late filing attracts ₹200/day penalty.
              </p>
            </div>

            <div className="faq-item">
              <h5>Can TDS be reduced or exempted?</h5>
              <p>
                Yes, apply for lower/nil TDS certificate from Income Tax Department if actual tax liability is lower. Submit Form 13 for application.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TDSCalculator;